import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/axios';
import withAuth from '../../auth';
import Spinner from 'react-bootstrap/Spinner';
import InputMask from 'react-input-mask';
import { CloseEye, OpenEye } from '../icons/Eye';
import useNotification from '../../hooks/useNotification';
import { handleApiRequest } from '../../utils/apiHandler';

const CreateUser = () => {
  const { showError, showSuccess } = useNotification();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone_num: '',
    address: '',

    business_name: '',
    designation: '',
    company_email: '',
    company_address: '',
  });

  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isCompanyUser, setIsCompanyUser] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleCompanyToggle = (e) => {
    const checked = e.target.checked;

    setIsCompanyUser(checked);

    if (!checked) {
      setFormData((prev) => ({
        ...prev,
        business_name: '',
        designation: '',
        company_email: '',
        company_address: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError('');
    setIsSubmitted(true);

    const payload = { ...formData };

    if (!isCompanyUser) {
      delete payload.business_name;
      delete payload.designation;
      delete payload.company_email;
      delete payload.company_address;
    }

    await handleApiRequest({
      requestFn: () => api.post(`/manager/users/create`, payload),
      onSuccess: (res) => {
        if (res.message) {
          showSuccess(res.message);
          navigate('/users');
        }
      },
      onError: (error) => {
        setError(error);
        showError(error);
      },
      finallyFn: () => {
        setIsSubmitted(false);
      },
    });
  };

  return (
      <div className="container mt-5">
        <h1 className="text-center">Create a new customer</h1>

        {error && <div className="alert alert-danger">{error}</div>}

        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card">
              <form
                  onSubmit={handleSubmit}
                  className="needs-validation card-body"
                  align="center"
                  noValidate
              >
                <div className="mb-3">
                  <label htmlFor="first_name" className="form-label">
                    First Name
                  </label>

                  <input
                      type="text"
                      className="form-control"
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="last_name" className="form-label">
                    Last Name
                  </label>

                  <input
                      type="text"
                      className="form-control"
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    Email
                  </label>

                  <input
                      type="email"
                      className="form-control"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                  />
                </div>

                <div className="mb-3 position-relative">
                  <label htmlFor="password" className="form-label">
                    Password
                  </label>

                  <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-control"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                  />

                  <button
                      type="button"
                      className="show-password-btn"
                      onClick={toggleShowPassword}
                      tabIndex={-1}
                  >
                    {showPassword ? <CloseEye /> : <OpenEye />}
                  </button>
                </div>

                <div className="mb-3">
                  <label htmlFor="phone_num" className="form-label">
                    Phone Number
                  </label>

                  <InputMask
                      className="form-control"
                      id="phone_num"
                      mask="+9 (999) 999-9999"
                      placeholder="+1 (999) 999-9999"
                      name="phone_num"
                      value={formData.phone_num}
                      onChange={handleChange}
                      required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="address" className="form-label">
                    Customer Address
                  </label>

                  <input
                      type="text"
                      className="form-control"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Customer address"
                  />
                </div>

                <hr />

                <div className="form-check mb-3 text-start">
                  <input
                      className="form-check-input"
                      type="checkbox"
                      id="is_company_user"
                      checked={isCompanyUser}
                      onChange={handleCompanyToggle}
                  />

                  <label className="form-check-label" htmlFor="is_company_user">
                    Create as company customer
                  </label>
                </div>

                {isCompanyUser && (
                    <>
                      <div className="mb-3">
                        <label htmlFor="business_name" className="form-label">
                          Business Name
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            id="business_name"
                            name="business_name"
                            value={formData.business_name}
                            onChange={handleChange}
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="designation" className="form-label">
                          Designation
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            id="designation"
                            name="designation"
                            value={formData.designation}
                            onChange={handleChange}
                            placeholder="Manager, Owner, Coordinator..."
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="company_email" className="form-label">
                          Company Email
                        </label>

                        <input
                            type="email"
                            className="form-control"
                            id="company_email"
                            name="company_email"
                            value={formData.company_email}
                            onChange={handleChange}
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="company_address" className="form-label">
                          Company Address
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            id="company_address"
                            name="company_address"
                            value={formData.company_address}
                            onChange={handleChange}
                            placeholder="Company address"
                        />
                      </div>
                    </>
                )}

                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitted}
                >
                  {isSubmitted ? (
                      <>
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                        />
                        &nbsp;Creating...
                      </>
                  ) : (
                      'Create'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
  );
};

export default withAuth(CreateUser);